import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getRouteApi, useRouter } from '@tanstack/react-router';

import { updateMediaImageName } from '@/api/media';
import { Button } from '@/components/ui/button';
import TimeSpinner from '@/components/ui/time-spinner';
import { uploadImage as uploadImageToEstateMedia } from '@/features/estate-media/api/upload-image';
import { getImage } from '@/features/image-rendering/api/image';
import { useRenderImage } from '@/features/image-rendering/api/render-image';
import { getRenderingImage as getRenderingImageApi } from '@/features/image-rendering/api/rendering-image';
import { useUploadImage } from '@/features/image-rendering/api/upload-image';
import { ImageEditor } from '@/features/image-rendering/components/editor';
import { ImageRenderingHeader } from '@/features/image-rendering/components/header';
import { ImageRenderingSidebar } from '@/features/image-rendering/components/side-bar';
import { StyleSettings } from '@/features/image-rendering/components/style-settings/style-settings';
import {
  EDIT_IMAGE_METHOD,
  MAGIC_TOOL_OPTIONS,
} from '@/features/image-rendering/constants/toggle-group-options';
import { useElementResize } from '@/hooks/use-element-resize';

const route = getRouteApi('/_authenticated/_sidebar-layout/image-rendering');

export type CarouselImage = {
  mediaId: string;
  sourceId?: string;
};

export const MIN_ZOOM = 1;

export const ImageRenderingPage = () => {
  const router = useRouter();
  if (!appConfig.imageRendering) {
    router.navigate({ to: '/' });
  }

  const { imageId: imageIdFromParams, estateId: estateIdFromParams } = route.useSearch();

  const { ref, size } = useElementResize();
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const pollingTimeout = useRef(null);
  const imgRef = useRef(null);
  const [originImageId, setOriginImageId] = useState<string | undefined>();
  const [prevImageId, setPrevImageId] = useState<string | undefined>();
  const [imageId, setImageId] = useState<string | undefined>(imageIdFromParams);
  const [selectedImage, setSelectedImage] = useState<CarouselImage | undefined>();
  const [taskId, setTaskId] = useState<string | undefined>();
  const [renderedImageIds, setRenderedImageIds] = useState<CarouselImage[]>(
    imageIdFromParams ? [{ mediaId: imageIdFromParams }] : []
  );
  const [fileName, setFileName] = useState<string | undefined>();
  const [editMethod, setEditMethod] = useState<EDIT_IMAGE_METHOD | null>(null);
  const [originSettings, setOriginSettings] = useState<StyleSettings>({});
  const [settings, setSettings] = useState<StyleSettings>({});
  const [magicToolOption, setMagicToolOption] = useState<MAGIC_TOOL_OPTIONS | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [estateId, setEstateId] = useState<string | undefined>(estateIdFromParams);
  const [showFloatingButton, setShowFloatingButton] = useState(false);
  const [showMobileSettings, setShowMobileSettings] = useState(false);

  const { mutate: uploadImage } = useUploadImage({
    mutationConfig: {
      onSuccess: (key: string) => {
        // This is a temporary workaround, so it can be deployed in a backend-compatible manner, because
        // the backend returns the ID in the format: `Put ${key} successfully!`
        // TODO: find a cleaner solution to get the image key together with Timon
        const words = key.split(' ');
        if (words.length >= 2) {
          const actualKey = words[1];
          if (!originImageId && !prevImageId) {
            setOriginImageId(imageId ?? actualKey);
            setPrevImageId(imageId ?? actualKey);
            setImageId(actualKey);
            setRenderedImageIds((prev: CarouselImage[]) => [...prev, { mediaId: actualKey }]);
          } else {
            setRenderedImageIds((prev: CarouselImage[]) => [
              ...prev,
              { mediaId: actualKey, sourceId: imageId },
            ]);
          }
        } else {
          console.warn("Key doesn't have a second word:", key);
        }
        setIsLoading(false);
      },
    },
  });

  const { mutate: renderImage } = useRenderImage({
    mutationConfig: {
      onSuccess: (res: { task_id: string }) => setTaskId(res.task_id),
    },
  });

  const didSettingsChange = useMemo(
    () => JSON.stringify(settings) !== JSON.stringify(originSettings),
    [settings, originSettings]
  );
  const didSourceChange = useMemo(() => imageId !== prevImageId, [imageId, prevImageId]);
  const isSettingsFilled = useMemo(
    () => !!settings.roomMode && !!settings.roomType && !!settings.desiredStyle,
    [settings]
  );

  const handleChangeEditMethod = (val?: EDIT_IMAGE_METHOD) => {
    if (val && !!imageId) {
      setEditMethod(val);
    }
  };

  const handleChangeMagicToolOption = (val?: MAGIC_TOOL_OPTIONS) => {
    if (val && !!imageId) {
      setMagicToolOption(val);
    }
  };

  const handleUploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setOriginImageId(undefined);
    setPrevImageId(undefined);
    const file = e.target.files?.[0];

    await uploadImage({ image: file });
    setFileName(file.name);

    e.target.value = '';
  };

  const handleStartImageRendering = async () => {
    if (!imageId) {
      return;
    }
    setPrevImageId(imageId);
    setIsLoading(true);
    const imageResponse = await getImage(imageId);
    const blob = await imageResponse.blob();
    const image = new File([blob], 'image', { type: blob.type || 'image/jpeg' });

    await renderImage({
      image,
      roomtype: settings.roomType,
      style: settings.desiredStyle,
      colors: settings.desiredColor,
      materials: settings.materials,
    });
  };

  const handleDownloadImage = async () => {
    if (selectedImage) {
      const imageResponse = await getImage(selectedImage.mediaId);
      const blob = await imageResponse.blob();
      const imageUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = 'downloaded_image.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleReplaceSelectedImage = () => {
    setPrevImageId(imageId);
    setImageId(selectedImage?.mediaId);
  };

  const handleSelectImage = (imgId: string) => {
    setPrevImageId(imageId);
    setImageId(imgId);
    setRenderedImageIds((prev) => [...prev, { mediaId: imgId }]);
  };

  const handleChangeFileName = async (newVal: string) => {
    if (estateId && selectedImage?.mediaId && newVal) {
      updateMediaImageName(estateId, selectedImage?.mediaId, newVal).then(() => {
        setFileName(newVal);
      });
    }
  };

  const handleClickFloatingButton = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth', // Enables smooth scrolling
      });
    }
  };

  const checkScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

      setShowFloatingButton(scrollTop + clientHeight + 40 < scrollHeight);
    }
  };

  useEffect(() => {
    if (!imageId) {
      setEditMethod(null);
      setMagicToolOption(null);
    } else if (!editMethod) {
      setEditMethod(EDIT_IMAGE_METHOD.STYLE);
    }
  }, [imageId, editMethod]);

  useEffect(() => {
    const pollRenderingImage = async () => {
      if (pollingTimeout.current) {
        clearTimeout(pollingTimeout.current);
      }
      const pollingResponse = await getRenderingImageApi(taskId as string);
      if (pollingResponse.status === 202) {
        pollingTimeout.current = setTimeout(pollRenderingImage, 10000);
        return;
      }
      const blob = await pollingResponse.blob();
      const type = pollingResponse.headers.get('Content-Type') || 'image/jpeg';
      const image = new File([blob], 'image', { type });

      if (estateId) {
        await uploadImageToEstateMedia({ image, estateId });
      }
      await uploadImage({ image });
      setFileName(image?.name ?? 'Undefined');
      setOriginSettings(settings);
      setShowMobileSettings(false);
    };

    if (!taskId) return;

    pollingTimeout.current = setTimeout(pollRenderingImage, 10000);

    return () => clearTimeout(pollingTimeout.current);
  }, [taskId, estateId, settings, uploadImage]);

  useEffect(() => {
    if (containerRef?.current) {
      const { scrollHeight, clientHeight } = containerRef.current;

      setShowFloatingButton(scrollHeight > clientHeight);
      setShowFloatingButton(scrollHeight > clientHeight + 1);
    }
  }, [size, containerRef]);

  useEffect(() => {
    let containerRefValue: Element | null = null;

    if (containerRef.current) {
      containerRef.current.addEventListener('scroll', checkScroll);
      containerRefValue = containerRef.current;
    }

    return () => {
      if (containerRefValue) {
        containerRefValue.removeEventListener('scroll', checkScroll);
      }
    };
  }, []);

  const sidebarRef = useCallback(
    (node: Element) => {
      if (!node) return;
      const resizeObserver = new ResizeObserver(() => {
        const { clientHeight } = node;

        if (
          (editMethod === EDIT_IMAGE_METHOD.STYLE && clientHeight < 500) ||
          (editMethod === EDIT_IMAGE_METHOD.MAGIC && clientHeight < 600)
        ) {
          setShowFloatingButton(false);
        }
      });
      resizeObserver.observe(node);
    },
    [editMethod]
  );

  return (
    <>
      <TimeSpinner isLoading={isLoading} />
      <div
        ref={containerRef}
        className="relative mr-0 h-full overflow-y-auto pr-0 sm:-mr-[46px] sm:pr-[46px]"
      >
        <div ref={ref} className="relative flex flex-1 flex-col">
          <ImageRenderingHeader
            imageId={imageId}
            estateId={estateId}
            imageName={fileName}
            onChangeImageName={handleChangeFileName}
            onBack={() => setShowMobileSettings(false)}
          />
          <div className="relative flex flex-1 flex-col items-start gap-2 p-1 sm:flex-row sm:gap-7 sm:p-4 lg:py-6 lg:pl-8 lg:pr-8">
            <ImageRenderingSidebar
              sidebarRef={sidebarRef}
              imageId={imageId}
              settings={settings}
              canGenerate={(didSettingsChange || didSourceChange) && isSettingsFilled}
              editMethod={editMethod}
              estateId={estateId}
              showMobileSettings={showMobileSettings}
              onChangeEstateId={setEstateId}
              disableSelectObject={!!estateIdFromParams}
              onChangeSettings={setSettings}
              onUploadImage={handleUploadImage}
              onSelectImage={handleSelectImage}
              onCreateImage={handleStartImageRendering}
              onReplaceSelected={handleReplaceSelectedImage}
              onChangeEditMethod={handleChangeEditMethod}
            />
            <ImageEditor
              imageId={imageId}
              editMethod={editMethod}
              magicToolOption={magicToolOption}
              imgRef={imgRef}
              showMobileSettings={showMobileSettings}
              originImageId={imageId}
              selectedImage={selectedImage}
              renderedImageIds={renderedImageIds}
              setShowMobileSettings={setShowMobileSettings}
              onChangeImage={setSelectedImage}
              onChangeMagicToolOption={handleChangeMagicToolOption}
              onDownload={handleDownloadImage}
            />
          </div>
        </div>
        {showFloatingButton ? (
          <Button
            className="sticky bottom-[60px] left-[50%] mt-2 h-[34px] w-[120px] flex-1 -translate-x-[50%] py-1 sm:left-[32px] sm:translate-x-0 lg:left-[121px]"
            onClick={handleClickFloatingButton}
          >
            {t('create')}
          </Button>
        ) : null}
      </div>
    </>
  );
};
